import { useReducer } from "react";

interface CartPinFlowState {
  digits: string[];
  verified: boolean;
  exempted: boolean;
  verifying: boolean;
  exemptionModalOpen: boolean;
  sendModalOpen: boolean;
}

type CartPinFlowAction =
  | { type: "SET_DIGITS"; payload: string[] | ((prev: string[]) => string[]) }
  | { type: "SET_VERIFIED"; payload: boolean }
  | { type: "SET_EXEMPTED"; payload: boolean }
  | { type: "SET_VERIFYING"; payload: boolean }
  | { type: "SET_EXEMPTION_MODAL_OPEN"; payload: boolean }
  | { type: "SET_SEND_MODAL_OPEN"; payload: boolean }
  | { type: "RESET" };

const initialCartPinFlowState: CartPinFlowState = {
  digits: ["", "", "", ""],
  verified: false,
  exempted: false,
  verifying: false,
  exemptionModalOpen: false,
  sendModalOpen: false,
};

function cartPinFlowReducer(state: CartPinFlowState, action: CartPinFlowAction): CartPinFlowState {
  switch (action.type) {
    case "SET_DIGITS":
      return {
        ...state,
        digits: typeof action.payload === "function" ? action.payload(state.digits) : action.payload,
      };
    case "SET_VERIFIED":
      return { ...state, verified: action.payload };
    case "SET_EXEMPTED":
      return { ...state, exempted: action.payload };
    case "SET_VERIFYING":
      return { ...state, verifying: action.payload };
    case "SET_EXEMPTION_MODAL_OPEN":
      return { ...state, exemptionModalOpen: action.payload };
    case "SET_SEND_MODAL_OPEN":
      return { ...state, sendModalOpen: action.payload };
    case "RESET":
      return initialCartPinFlowState;
    default:
      return state;
  }
}

// Groups the cart-PIN verification flow (digits, verified/exempted/verifying flags,
// exemption + send-pin modals) so the six related flags reset and transition together.
export const useCartPinFlowState = () => {
  const [state, dispatch] = useReducer(cartPinFlowReducer, initialCartPinFlowState);

  return {
    cartUserPinDigits: state.digits,
    setCartUserPinDigits: (payload: string[] | ((prev: string[]) => string[])) =>
      dispatch({ type: "SET_DIGITS", payload }),
    isCartPinVerified: state.verified,
    setIsCartPinVerified: (payload: boolean) => dispatch({ type: "SET_VERIFIED", payload }),
    isCartPinExempted: state.exempted,
    setIsCartPinExempted: (payload: boolean) => dispatch({ type: "SET_EXEMPTED", payload }),
    isCartPinVerifying: state.verifying,
    setIsCartPinVerifying: (payload: boolean) => dispatch({ type: "SET_VERIFYING", payload }),
    openCartPinExemptionModal: state.exemptionModalOpen,
    setOpenCartPinExemptionModal: (payload: boolean) => dispatch({ type: "SET_EXEMPTION_MODAL_OPEN", payload }),
    isCartPinSendModalOpen: state.sendModalOpen,
    setIsCartPinSendModalOpen: (payload: boolean) => dispatch({ type: "SET_SEND_MODAL_OPEN", payload }),
    resetCartPinFlow: () => dispatch({ type: "RESET" }),
  };
};
